import { Link } from "react-router-dom";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { formatRate } from "../../utils/formatRate";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import { PricingSectionHeader, RateAmount } from "./PricingSectionParts";
import type { PublicRateCard, PublicRateLine } from "./rateCardTypes";

interface Props {
    tone: PricingTone;
    card: PublicRateCard;
}

const NOTES = [
    "Each call is rounded up to the next whole minute.",
    "Inbound and outbound calls are priced the same.",
    "Prepaid credits are used before anything is charged.",
];

function cell(line: PublicRateLine | undefined, tone: PricingTone, empty: string) {
    return line ? <RateAmount line={line} tone={tone} /> : <span className={`text-xs ${pricingTheme(tone).muted}`}>{empty}</span>;
}

export default function AiVoiceRates({ tone, card }: Props) {
    const t = pricingTheme(tone);
    const minutes = card.voice.filter((l) => l.chargeType === "VOICE_PER_MINUTE");
    const isInr = card.currency === "inr";

    return (
        <section aria-labelledby="pricing-ai-voice" className={t.section}>
            <PricingSectionHeader
                tone={tone}
                icon={PhoneIcon}
                id="pricing-ai-voice"
                title="AI voice agents"
                subtitle="Per minute your AI agent spends on a call, on top of your plan."
            />

            {isInr ? (
                <InrVoice tone={tone} card={card} />
            ) : minutes.length === 0 ? (
                <p className={`text-sm ${t.body}`}>
                    Voice pricing depends on the carrier you call on.{" "}
                    <Link to="/contact" className={`font-semibold ${t.link}`}>
                        Talk to us
                    </Link>{" "}
                    for a quote.
                </p>
            ) : (
                <div className={`overflow-x-auto rounded-lg border ${t.divider}`}>
                    <table className={`min-w-full divide-y ${t.divider}`}>
                        <caption className="sr-only">AI voice rates by carrier</caption>
                        <thead>
                            <tr className={`text-xs uppercase tracking-wide ${t.tableHead}`}>
                                <th scope="col" className="px-4 py-3 text-left font-semibold">
                                    Carrier
                                </th>
                                <th scope="col" className="px-4 py-3 text-right font-semibold">
                                    AI minute
                                </th>
                                <th scope="col" className="px-4 py-3 text-right font-semibold">
                                    Phone number
                                </th>
                                <th scope="col" className="px-4 py-3 text-right font-semibold">
                                    Warm transfer
                                </th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${t.divider}`}>
                            {minutes.map((minute) => {
                                const forCarrier = (type: string) => card.voice.find((l) => l.chargeType === type && l.carrier === minute.carrier);
                                return (
                                    <tr key={minute.carrier ?? "all"}>
                                        <th scope="row" className={`px-4 py-3 text-left text-sm font-medium ${t.subheading}`}>
                                            {minute.carrierLabel ?? "Any carrier"}
                                        </th>
                                        <td className="px-4 py-3 text-right">{cell(minute, tone, "")}</td>
                                        <td className="px-4 py-3 text-right">{cell(forCarrier("NUMBER_RENTAL"), tone, "Bring your own")}</td>
                                        <td className="px-4 py-3 text-right">{cell(forCarrier("CALL_TRANSFER"), tone, "Not charged")}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ul className={`mt-4 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3 ${t.muted}`}>
                {NOTES.map((note) => (
                    <li key={note}>{note}</li>
                ))}
            </ul>
        </section>
    );
}

function InrVoice({ tone, card }: Props) {
    const t = pricingTheme(tone);
    const minute = card.voice.find((l) => l.chargeType === "VOICE_PER_MINUTE");
    const transfer = card.voice.find((l) => l.chargeType === "CALL_TRANSFER");
    const committed = card.committedVoice;

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className={t.card}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${t.muted}`}>Any carrier — Jio, Vi, Exotel and more</p>
                <div className="mt-2">{minute ? <RateAmount line={minute} tone={tone} large /> : null}</div>
                {transfer && (
                    <p className={`mt-3 text-sm ${t.body}`}>
                        Warm transfer to a human: <RateAmount line={transfer} tone={tone} />
                    </p>
                )}
                <p className={`mt-3 text-xs ${t.muted}`}>Calls run on Indian numbers you already own, so there is no number rental.</p>
            </div>
            {committed && (
                <div className={`${t.card} flex flex-col`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${t.accent}`}>Committed volume</p>
                    <p className="mt-2">
                        <span className={`text-3xl font-extrabold tabular-nums ${t.heading}`}>{formatRate(committed.perMinuteMinor, card.currency)}</span>
                        <span className={`ml-1 text-base ${t.muted}`}>/ min</span>
                    </p>
                    <p className={`mt-3 text-sm ${t.body}`}>
                        On a committed plan of {committed.minMonthlyMinutes.toLocaleString("en-IN")}+ minutes a month.
                    </p>
                    <Link to="/contact" className={`mt-auto pt-4 text-sm font-semibold ${t.link}`}>
                        Talk to sales
                    </Link>
                </div>
            )}
        </div>
    );
}
